'use client';

import {
  type ClipboardEvent as ReactClipboardEvent,
  type DragEvent as ReactDragEvent,
  useCallback,
  useRef,
  useState,
} from 'react';

interface UseFileDropParams {
  /**
   * Receives the dropped/pasted files. The caller owns ALL validation (MIME,
   * size, count) and the upload itself — this hook never touches storage.
   */
  onFiles: (files: FileList | File[]) => void;

  /** When true, drag highlighting and both handlers are inert. */
  disabled?: boolean;
}

/**
 * Headless drag-and-drop + clipboard-paste plumbing for file attachments.
 *
 * Deliberately does NOT upload: every attachment surface in this app writes to
 * a different bucket under a different path, and some (bug reports) can't build
 * the path until the parent row exists at submit time. `@kit/ui/dropzone` is
 * coupled to `useSupabaseUpload` and so can't serve those callers.
 *
 * Spread `dropProps` on the drop target and `pasteProps` on the textarea:
 *
 *   const { dropProps, pasteProps, isDragOver } = useFileDrop({ onFiles });
 *   <div {...dropProps} data-drag={isDragOver}>
 *     <Textarea {...pasteProps} />
 *   </div>
 */
export function useFileDrop({ onFiles, disabled = false }: UseFileDropParams) {
  const [isDragging, setIsDragging] = useState(false);
  // dragenter/dragleave fire for every descendant the cursor crosses, so a
  // boolean alone flickers off when moving over a child. Counting enters and
  // leaves is what keeps the highlight stable over nested content.
  const dragCounter = useRef(0);

  const reset = useCallback(() => {
    dragCounter.current = 0;
    setIsDragging(false);
  }, []);

  // Derived, not stored: becoming disabled mid-gesture drops the highlight on
  // the very next render, with no effect and no cascading setState. The raw
  // `isDragging` flag stays untouched so an enabled-again composer resumes
  // correctly if the pointer is still over it.
  const isDragOver = isDragging && !disabled;

  const onDragEnter = useCallback(
    (event: ReactDragEvent<HTMLElement>) => {
      if (disabled) return;
      event.preventDefault();
      event.stopPropagation();
      dragCounter.current += 1;
      // types contains 'Files' only when the drag originates from the OS —
      // dragging selected text or an image within the page must not highlight.
      if (event.dataTransfer.types.includes('Files')) {
        setIsDragging(true);
      }
    },
    [disabled],
  );

  const onDragLeave = useCallback((event: ReactDragEvent<HTMLElement>) => {
    // Deliberately NOT gated on `disabled`: unwinding the counter is cleanup,
    // not a privileged action. `disabled` can flip true mid-gesture (the user
    // hits submit while still holding a file over the zone), and bailing here
    // would strand dragCounter/isDragOver — leaving the highlight lit until
    // some unrelated later drag happens to zero the counter again.
    event.preventDefault();
    event.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const onDragOver = useCallback(
    (event: ReactDragEvent<HTMLElement>) => {
      if (disabled) return;
      event.preventDefault();
      event.stopPropagation();
      // Without this the OS shows a "no drop" cursor and never fires onDrop.
      event.dataTransfer.dropEffect = 'copy';
    },
    [disabled],
  );

  const onDrop = useCallback(
    (event: ReactDragEvent<HTMLElement>) => {
      // preventDefault unconditionally: a file dropped anywhere in the page
      // otherwise navigates the browser to it, losing unsaved form state.
      event.preventDefault();
      event.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);
      if (disabled) return;
      if (event.dataTransfer.files.length > 0) {
        onFiles(event.dataTransfer.files);
      }
    },
    [disabled, onFiles],
  );

  const onPaste = useCallback(
    (event: ReactClipboardEvent<HTMLElement>) => {
      if (disabled) return;

      // A clipboard carrying both text and a file (copying a file in Finder
      // yields a filename string too) must still paste the text, so only the
      // 'file' kind is collected and the event is left alone when there is none.
      const files = Array.from(event.clipboardData.items)
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((file): file is File => file !== null);

      if (files.length === 0) return;

      // A screenshot pasted from the clipboard has no filename — it arrives as
      // "image.png" on every paste. Stamping the name keeps chips
      // distinguishable and stops same-name collisions in the storage path.
      const stamped = files.map((file) => {
        if (file.name && file.name !== 'image.png') return file;
        // Subtype only, stripped of any suffix/parameters ('image/svg+xml' ->
        // 'svg') so the stamped name can never carry '+' or ';' into a
        // storage key. Callers still MIME-validate; this only names the file.
        const subtype = file.type.split('/')[1]?.split(/[+;]/)[0];
        const extension = subtype?.replace(/[^a-z0-9]/gi, '') || 'png';
        return new File([file], `pasted-${Date.now()}.${extension}`, {
          type: file.type,
          lastModified: file.lastModified,
        });
      });

      event.preventDefault();
      onFiles(stamped);
    },
    [disabled, onFiles],
  );

  return {
    isDragOver,
    reset,
    dropProps: { onDragEnter, onDragLeave, onDragOver, onDrop },
    pasteProps: { onPaste },
  };
}
